import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useDebounceValue } from 'usehooks-ts';
import useCorrelationsStore from '../useCorrelationsStore';
import { standardsQueryKey } from '../standardsFilterUtils';
import { fetchStandards } from './useStandardsTree';

export const MIN_SEARCH_LENGTH = 2;
export const SEARCH_DEBOUNCE_MS = 400;

interface UseStandardsSearchParams {
  grade: string | number | null;
  programId: number;
  stateId: number;
}

// Server-side search over the whole pruned standards tree: the filter panel only
// lazy-loads expanded levels, so matching has to happen where the full tree lives. The response
// is the matching subtrees — hits plus their ancestors and descendants — rendered in place.
const useStandardsSearch = ({ grade, programId, stateId }: UseStandardsSearchParams) => {
  const rawQuery = useCorrelationsStore(s => s.standardsQuery);
  const mergeStandardsNodes = useCorrelationsStore(s => s.mergeStandardsNodes);
  const [term] = useDebounceValue(rawQuery.trim(), SEARCH_DEBOUNCE_MS);
  // Entering search waits for the debounce (term), but leaving follows the raw input so clearing
  // restores the lazy tree immediately instead of 400ms later.
  const searching = rawQuery.trim().length >= MIN_SEARCH_LENGTH && term.length >= MIN_SEARCH_LENGTH;

  const query = useQuery({
    enabled: searching,
    queryFn: () => fetchStandards({ grade, program_id: programId, search: term, state_id: stateId }),
    queryKey: [...standardsQueryKey(stateId, programId), grade, 'search', term],
    staleTime: 5 * 60 * 1000,
  });

  const nodes = query.data?.data;

  // The check cascade derives its parent/children maps from the store, so search nodes must be
  // merged for checks on never-expanded standards to behave.
  useEffect(() => {
    if (nodes) mergeStandardsNodes(nodes);
  }, [nodes, mergeStandardsNodes]);

  return { isError: query.isError, isLoading: query.isInitialLoading, nodes, searching, term };
};

export default useStandardsSearch;
